import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import CustomInputField from "components/common/CustomInputField";
import { CreateProductAPI } from "network/productAPI";
import { successToaster, ErrorToaster } from "components/common/toast";
import handleError from "utils/handleError";
import BtnCustom from "components/common/btn-custom";
import { useEffect, useState } from "react";
import { GetSectionSubCategoriesAPI } from "network/categoryAPI";
import CustomPaginatedSelect from "components/common/CustomPaginatedSelect";
import { UpdateProductAPI } from "network/productAPI";
import { ProductSchema } from "components/schema/ProductSchema";
import AttributesFrom from "views/products/AttributesFrom";
import { GetSectionCategoriesAPI } from "network/categoryAPI";

const ProductForm = ({ sectionData, editData }) => {
  const { t } = useTranslation("products");
  const [productId, setProductId] = useState("");
  const resetState = () => {
    setProductId("");
  };
  useEffect(() => {
    resetState();
  }, [sectionData]);
  const ProductFieldsArray = [
    { name: "name", type: "text", schemaType: "string", required: true },
    {
      name: "price",
      type: "text",
      schemaType: "number",
      required: true,
    },
    {
      name: "stock",
      type: "text",
      schemaType: "number",
      required: true,
    },
    {
      name: "description",
      type: "textarea",
      schemaType: "string",
      required: true,
    },
  ];
  const formattedCategories =
    editData?.categories && editData.categories.length > 0
      ? editData.categories.length === 1
        ? {
            value: editData.categories[0]?.uuid,
            label: editData.categories[0]?.name,
          }
        : editData.categories.map((item) => ({
            value: item?.uuid,
            label: item?.name,
          }))
      : "";

  const formattedSubCategories =
    editData?.sub_categories && editData.sub_categories.length > 0
      ? editData.sub_categories.length === 1
        ? {
            value: editData.sub_categories[0]?.uuid,
            label: editData.sub_categories[0]?.name,
          }
        : editData.sub_categories.map((item) => ({
            value: item?.uuid,
            label: item?.name,
          }))
      : "";

  const initialValues = {
    name: editData?.name || "",
    description: editData?.description || "",
    price: editData?.price || "",
    stock: editData?.stock || "",
    categories: formattedCategories,
    sub_categories: formattedSubCategories,
  };
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    resetForm,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: ProductSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("price", values.price);
      formData.append("stock", values.stock);
      formData.append("categories", values.categories?.value || "");
      formData.append("sub_categories", values.sub_categories?.value || "");
      try {
        if (sectionData?.uuid) {
          if (editData) {
            const response = await UpdateProductAPI(
              sectionData?.uuid,
              editData?.uuid,
              formData
            );
            if (response.success) {
              setProductId(response?.data);
              successToaster(response.message);
            }
          } else {
            const response = await CreateProductAPI(
              sectionData?.uuid,
              formData
            );
            if (response.success) {
              setProductId(response?.data);
              successToaster(response.message);
              resetForm();
            }
          }
        } else {
        }
      } catch (error) {
        ErrorToaster(handleError(error?.status_code, error?.message));
      }
    },
  });

  const loadCategoryOptions = async (
    search,
    prevOptions = [],
    additional = { page: 1 }
  ) => {
    const page = additional.page || 1;
    try {
      const response = await GetSectionCategoriesAPI(
        sectionData?.uuid,
        page,
        10,
        search
      );
      if (response.success) {
        const newOptions = response?.data?.categories.map((category) => ({
          value: category?.uuid,
          label: category?.name,
        }));
        return {
          options: [...newOptions],
          hasMore: response?.data?.has_next,
          additional: { page: page + 1 },
        };
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    return {
      options: prevOptions,
      hasMore: false,
      additional: { page: page + 1 },
    };
  };
  const loadSubCategoryOptions = async (
    search,
    prevOptions = [],
    additional = { page: 1 }
  ) => {
    const page = additional.page || 1;
    try {
      const response = await GetSectionSubCategoriesAPI(
        sectionData?.uuid,
        page,
        10,
        search
      );
      if (response.success) {
        const newOptions = response?.data?.sub_categories.map((category) => ({
          value: category.uuid,
          label: category.name,
        }));
        return {
          options: [...newOptions],
          hasMore: response?.data?.has_next,
          additional: { page: page + 1 },
        };
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    return {
      options: prevOptions,
      hasMore: false,
      additional: { page: page + 1 },
    };
  };
  const handleReset = () => {
    resetForm({ values: { ...initialValues } });
  };
  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-12 col-sm-6 col-md-6 col-lg-3 mb-2">
            <CustomPaginatedSelect
              label={t("form.category")}
              name="categories"
              errors={errors}
              touched={touched}
              onBlur={handleBlur}
              onChange={(selectedOption) => {
                setFieldValue("categories", selectedOption || "");
              }}
              value={values["categories"] || []}
              loadOptions={loadCategoryOptions}
            />
          </div>

          <div className="col-12 col-sm-6 col-md-6 col-lg-3 mb-2">
            <CustomPaginatedSelect
              label={t("form.subCategory")}
              name="sub_categories"
              errors={errors}
              touched={touched}
              onChange={(selectedOption) => {
                setFieldValue("sub_categories", selectedOption || "");
              }}
              onBlur={handleBlur}
              value={values["sub_categories"] || []}
              loadOptions={loadSubCategoryOptions}
            />
          </div>
          {ProductFieldsArray.map(({ name, type, required }) => {
            return (
              <div
                key={name}
                className="col-12 col-sm-6 col-md-6 col-lg-3 mb-2"
              >
                <CustomInputField
                  label={`${t(`form.fields.${name}`)}${required ? "*" : ""}`}
                  name={name}
                  placeholder={t(`form.fields.${name}`)}
                  type={type}
                  errors={errors}
                  touched={touched}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values}
                />
              </div>
            );
          })}
        </div>
        <BtnCustom
          isLoading={isSubmitting}
          type="submit"
          color="btn btn-primary mt-4 block mr-3"
          submittingText={editData ? t("form.updating") : t("form.adding")}
        >
          {editData ? t("form.update") : t("form.add")}
        </BtnCustom>
        {!editData && (
          <BtnCustom
            disabled={isSubmitting}
            type="button"
            color="btn btn-secondary mt-4 block text-white"
            onClick={handleReset}
          >
            {t("form.reset")}
          </BtnCustom>
        )}
      </form>
      <div className="border-top border-light my-5"></div>

      <AttributesFrom
        sectionData={sectionData}
        editData={editData?.attributes}
        productId={editData?.uuid || productId}
      />
    </>
  );
};

export default ProductForm;
